<template>
  <v-dialog content-class="issue-details-popup"
            v-if="visiblePopup"
            :value="visiblePopup"
            scrollable
            @click:outside='onCloseClickButton'
            @keydown.esc="onCloseClickButton">
    <v-card class="popup-wrapper">
      <div class="popup-container">
        <div class="main-part">
          <div class="header-line">
            <div class="security-risk">
              <div class="security-risk-description">
                {{issueFormattedName}}
              </div>
              <div class='suppress-issue' v-if='!isSuppressed'>
                <v-btn class='toggle-suppress-issue' :disabled='isSuppressIssueDisabled' @click='$emit("suppress", issue, null)'>
                  {{ $t('detailed.issue.action.suppress') }}
                </v-btn>
              </div>
              <!-- Same spot as Suppress, so undoing it is where the user expects. -->
              <div class='suppress-issue' v-else>
                <v-btn class='toggle-suppress-issue' @click='$emit("restore", issue)'>
                  {{ $t('detailed.issue.action.unsuppress') }}
                </v-btn>
              </div>
            </div>
            <button type="button"
                    class="close"
                    :aria-label="$t('close')"
                    @click="onCloseClickButton">
              <span class="material-icons-outlined" aria-hidden="true">close</span>
            </button>
          </div>

          <div class="data-row">
            <div class="data-column">
              <div class="data-title" v-if='issue && issue.severity'>{{$t('issueDataPopup.severity')}}</div>
              <issue-severity-chip v-if='issue && issue.severity' class="severity-risk-score-line" :issue='issue'/>
              <issue-risk-score v-if='issue.exploit' class="severity-risk-score-line issue-risk-score" :issue="issue"/>
            </div>
            <div class="data-column issue-status" v-if='issue && issue.status'>
              <div class="data-title">{{$t('issueDetailsPopup.status')}}</div>
              <centralized-issue-status :status="statusKey"/>
            </div>
            <div class="data-column-date">
              <div class="data-title">{{$t('issueDetailsPopup.first.seen')}}</div>
              <span class="date-column">
                {{firstSeen}}
              </span>
            </div>
            <div class="data-column-date">
              <div class="data-title">{{$t('issueDetailsPopup.last.seen')}}</div>
              <span class="date-column">
                {{lastSeen}}
              </span>
            </div>
          </div>

          <!-- One-line summary: the key facts without a click. Only when suppressed. -->
          <div class='suppression-summary' v-if='currentSuppression'>
            <span class='suppression-summary__text'>{{ suppressionSummary }}</span>
          </div>

          <div class='issue-tabs' role='tablist' :aria-label='$t("issueDetailsPopup.tabs.label")' v-if='tabs.length > 1'>
            <button
              v-for='(tab, index) in tabs'
              :key='tab.id'
              :id='`issue-tab-${tab.id}`'
              ref='tabButtons'
              type='button'
              role='tab'
              class='issue-tabs__tab'
              :class='{ "issue-tabs__tab--active": activeTab === tab.id }'
              :aria-selected='String(activeTab === tab.id)'
              :aria-controls='`issue-tabpanel-${tab.id}`'
              :tabindex='activeTab === tab.id ? 0 : -1'
              @click='selectTab(tab.id)'
              @keydown='onTabKeydown($event, index)'
            >
              {{ tab.label }}
              <span class='issue-tabs__dot' v-if='tab.dot' aria-hidden='true' />
            </button>
          </div>
        </div>
        <div
          class='rest-part'
          :class="activeTab === 'discovery' ? null : getWhiteLabeling"
          :id='`issue-tabpanel-${activeTab}`'
          :role='tabs.length > 1 ? "tabpanel" : null'
          :aria-labelledby='tabs.length > 1 ? `issue-tab-${activeTab}` : null'
        >
          <div class='details-body-section' v-show='activeTab === "overview"'>
            <slot name="issue-body" :issue="issue" @closed='onCloseClickButton'>
              <default-detailed-issue-body :issue="issue"/>
            </slot>
          </div>
          <suppression-panel
            v-if='activeTab === "suppression"'
            :history='suppressionHistory'
            :fallback='issue.suppression || null'
            @edit='current => $emit("suppress", issue, current)'
          />
          <discovery-history
            v-if='activeTab === "discovery" && discoveryHistory'
            :history='discoveryHistory'
            :suppression-history='suppressionHistory'
          />
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex';
import moment from 'moment';

import { userRoleGroups } from '@/constants/auth';
import { getSecurityRiskDescription } from '@/utils/stringUtils';
import IssueRiskScore from '@/components/issue/IssueRiskScore.vue';
import {
  CLEAR_DETAILED_ISSUES_FOR_CENTRALIZED_VIEW, SET_OPEN_DETAILED_ISSUES_POPUP,
} from '@/store/centralizedIssues/actionTypes';
import DefaultDetailedIssueBody from '@/components/DetailedIssuesBody/DefaultDetailedIssueBody.vue';
import { CLEAR_ISSUE_DETAILS } from '@/store/issue/actionTypes';
import IssueSeverityChip from '@/components/common/IssueSeverityChip.vue';
import CentralizedIssueStatus from '@/components/common/CentralizedIssueStatus.vue';
import SuppressionPanel from '@/components/suppression/SuppressionPanel.vue';
import DiscoveryHistory from '@/components/issue/DiscoveryHistory.vue';
import { SUPPRESSION_ORIGINS, SUPPRESSION_REASONS, SUPPRESSION_TYPES } from '@/constants/suppression';
import disableOuterScrollOnPopupVisible from '@/mixins/disableOuterScrollOnPopupVisible';
// eslint-disable-next-line import/no-cycle
import { getIssueDetails } from '@/services/repository';
import { daysUntilExpiry, formatSuppressionDate, issueStatusKey } from '@/utils/suppression';

const ISSUE_DETAILS_DATE_FORMAT = 'DD MMM YYYY hh:mm';

export default {
  name: 'AutomaticIssueDetailsPopup',
  mixins: [disableOuterScrollOnPopupVisible],
  components: {
    CentralizedIssueStatus,
    SuppressionPanel,
    DiscoveryHistory,
    IssueSeverityChip,
    DefaultDetailedIssueBody,
    IssueRiskScore,
  },
  props: {
    visiblePopup: {
      type: Boolean,
      required: true,
    },
    issue: {
      type: Object,
      required: true,
    },
    /** Bump to reload the suppression and discovery history (after a suppress / restore). */
    refreshKey: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      suppressionHistory: [],
      discoveryHistory: null,
      activeTab: 'overview',
    };
  },
  watch: {
    visiblePopup: {
      immediate: true,
      handler(visible) {
        this.$store.dispatch(SET_OPEN_DETAILED_ISSUES_POPUP, false);
        if (visible) {
          this.activeTab = 'overview';
          this.loadHistory();
        }
      },
    },
    'issue.id': function onIssueChanged() {
      this.activeTab = 'overview';
      if (this.visiblePopup) {
        this.loadHistory();
      }
    },
    tabs(tabs) {
      if (!tabs.some(tab => tab.id === this.activeTab)) {
        this.activeTab = 'overview';
      }
    },
    refreshKey() {
      if (this.visiblePopup) {
        this.loadHistory();
      }
    },
  },
  computed: {
    ...mapGetters(['issueDetails', 'userRoleGroup', 'whiteLabelingConfig']),
    userGroups() {
      return userRoleGroups;
    },
    isSuppressIssueDisabled() {
      return !this.issue || !this.issue.id;
    },
    isSuppressed() {
      return !!(this.issue && this.issue.suppression);
    },
    statusKey() {
      return issueStatusKey(this.issue);
    },
    /** Effective suppression: from the loaded history, else what the list row already carries. */
    currentSuppression() {
      return this.suppressionHistory.find(entry => entry.isEffective) || (this.issue && this.issue.suppression) || null;
    },
    suppressionSummary() {
      const s = this.currentSuppression;
      if (!s) {
        return '';
      }
      const parts = [];
      if (s.origin === SUPPRESSION_ORIGINS.BACKFILL) {
        parts.push(this.$t('suppression.summary.backfilled'));
      } else {
        parts.push(this.$t('suppression.summary.by', {
          name: s.suppressedByName || this.$t('suppression.panel.unknown.user'),
        }));
        parts.push(formatSuppressionDate(s.suppressedAt));
      }
      const reason = this.$t(`suppression.reason.${s.reason}`);
      parts.push(s.reason === SUPPRESSION_REASONS.OTHER && s.reasonOther ? `${reason}: ${s.reasonOther}` : reason);
      if (s.type === SUPPRESSION_TYPES.TEMPORARY) {
        const days = daysUntilExpiry(s);
        parts.push(days === 0
          ? this.$t('suppression.summary.expires.today', { date: formatSuppressionDate(s.expiresAt) })
          : this.$tc('suppression.summary.expires', days, { date: formatSuppressionDate(s.expiresAt), days }));
      } else {
        parts.push(this.$t('suppression.expiry.none'));
      }
      return parts.join(' · ');
    },
    tabs() {
      const tabs = [{ id: 'overview', label: this.$t('issueDetailsPopup.tabs.overview') }];
      if (this.currentSuppression || this.suppressionHistory.length) {
        tabs.push({ id: 'suppression', label: this.$t('issueDetailsPopup.tabs.suppression'), dot: !!this.currentSuppression });
      }
      if (this.discoveryHistory && this.discoveryHistory.summary && this.discoveryHistory.summary.cycles > 0) {
        tabs.push({ id: 'discovery', label: this.$t('issueDetailsPopup.tabs.discovery') });
      }
      return tabs;
    },
    securityRiskCode() {
      return this.issue.id;
    },
    securityRiskDescription() {
      return getSecurityRiskDescription(this.issue.severity);
    },
    lastSeen() {
      return moment(this.issue.date).format(ISSUE_DETAILS_DATE_FORMAT);
    },
    firstSeen() {
      return moment(this.issue.firstSeen).format(ISSUE_DETAILS_DATE_FORMAT);
    },
    createdDate() {
      return moment(this.issue.date).format(ISSUE_DETAILS_DATE_FORMAT);
    },
    updatedDate() {
      return moment(this.issue.date).format(ISSUE_DETAILS_DATE_FORMAT);
    },
    reference() {
      return this.issue.reference;
    },
    solutions() {
      return this.issue.solution?.filter(solution => solution?.length > 0) || [];
    },
    issueFormattedName() {
      // For CVE issues, prefer the enrichment display_name which contains the full CVE description
      const enrichmentDisplayName = (this.issue.metaData && this.issue.metaData.enrichment && this.issue.metaData.enrichment.display_name) || '';
      if (enrichmentDisplayName) {
        return enrichmentDisplayName;
      }
      let name = `${this.issue.displayName || this.issue.name}`;
      name = name.replace(/\waf\b/, 'WAF');
      return name;
    },
    urls() {
      if (this.issue.technicalData?.url) {
        if (this.issue.technicalData.url.length > 5) {
          return this.issue.technicalData.url.slice(0, 5);
        }
        return this.issue.technicalData.url;
      }
      return [];
    },
    evidences() {
      return this.issue.technicalData?.evidence || [];
    },
    getWhiteLabeling() {
      return this.whiteLabelingConfig?.domain?.replace(/[-.]/g, '_') || 'cynergy_app';
    },
  },
  methods: {
    selectTab(id) {
      if (this.tabs.some(tab => tab.id === id)) {
        this.activeTab = id;
      }
    },
    /** Arrow keys move between tabs (WAI-ARIA tabs pattern). */
    onTabKeydown(event, index) {
      const keys = { ArrowRight: 1, ArrowLeft: -1 };
      let next = null;
      if (keys[event.key]) {
        next = (index + keys[event.key] + this.tabs.length) % this.tabs.length;
      } else if (event.key === 'Home') {
        next = 0;
      } else if (event.key === 'End') {
        next = this.tabs.length - 1;
      }
      if (next === null) {
        return;
      }
      event.preventDefault();
      this.activeTab = this.tabs[next].id;
      this.$nextTick(() => {
        const buttons = this.$refs.tabButtons || [];
        if (buttons[next]) {
          buttons[next].focus();
        }
      });
    },
    /**
     * Suppression and discovery history are per finding and read on demand here,
     * so they are never carried on every issue row.
     */
    async loadHistory() {
      const issueId = this.issue && this.issue.id;
      if (!issueId) {
        return;
      }
      try {
        const details = await getIssueDetails(issueId);
        if (!this.issue || this.issue.id !== issueId) {
          return;
        }
        this.suppressionHistory = details?.suppressionHistory || [];
        this.discoveryHistory = details?.discoveryHistory || null;
        const current = this.suppressionHistory.find(entry => entry.isEffective) || null;
        this.$emit('suppressionLoaded', current);
      } catch (e) {
        // Keep what the list already knows (issue.suppression drives the panel's fallback).
        this.suppressionHistory = [];
        this.discoveryHistory = null;
        // eslint-disable-next-line no-console
        console.warn('Issue details (suppression / discovery history) failed to load', e);
      }
    },
    async onCloseClickButton() {
      await this.$store.dispatch(CLEAR_ISSUE_DETAILS);
      await this.$store.dispatch(CLEAR_DETAILED_ISSUES_FOR_CENTRALIZED_VIEW);
      this.$emit('closed', true);
    },
  },
};
</script>
<style lang="scss">
.v-dialog.issue-details-popup{
  max-width: 810px;
  overflow-y: hidden;
}
.data-row {
  .data-column {
    .centralized-issue-status__status__indicator {
      margin-top: 1px;
    }
    // "Temporary exception" stays on one line.
    &.issue-status {
      max-width: none;
      .centralized-issue-status__status__label {
        white-space: nowrap;
      }
    }
  }
}
.details-body-section {
  .material-icons.bullet {
    font-size: 15px;
    opacity: 0.5;
    margin-top: 3px;
  }
}
</style>
<style lang="scss" scoped>
@import '../../assets/styles/design-system';
.popup-wrapper {
  background: var(--bg-secondary);
  overflow-y: hidden;
}
.popup-container {
  min-height: 447px;
  max-height: 887px;
  border-radius: 4px;
  background-color: var(--bg-primary);
  box-shadow: 0 0 11px 0 rgba(12,18,62,0.13);
  position: relative;
  margin: auto;
  width: 100%;
  .close {
    cursor: pointer;
    display: flex;
    position: absolute;
    right: $space-2;
    top: $space-2;
    padding: 0;
    background: none;
    border: none;
    color: var(--text-secondary);

    &:hover,
    &:focus-visible {
      color: var(--brand-primary);
    }
  }

  .suppression-summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: $space-4;
    margin: rem(-20) $space-8 $space-4 0;
    padding: $space-2 $space-3;
    border-radius: $radius-sm;
    background-color: var(--bg-tertiary);
    color: var(--text-primary);
    font-size: $font-size-sm;

    &__text {
      overflow-wrap: anywhere;
    }
  }

  .issue-tabs {
    display: flex;
    gap: $space-6;

    &__tab {
      position: relative;
      display: inline-flex;
      align-items: center;
      gap: rem(6);
      padding: $space-2 0 $space-3;
      background: none;
      border: none;
      color: var(--text-secondary);
      font-size: $font-size-sm;
      font-family: $font-family-medium;
      letter-spacing: 0.4px;
      cursor: pointer;
      transition: color 150ms ease;

      // Underline grows from the center of the tab, transform only.
      &::after {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        height: 2px;
        background-color: var(--brand-primary);
        transform: scaleX(0);
        transition: transform 200ms cubic-bezier(0.23, 1, 0.32, 1);
      }

      &:hover {
        color: var(--text-primary);
      }

      &:focus-visible {
        outline: 2px solid var(--brand-primary);
        outline-offset: 2px;
      }

      &--active {
        color: var(--text-primary);

        &::after {
          transform: scaleX(1);
        }
      }
    }

    &__dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background-color: #8a8f98;
    }

    @media (prefers-reduced-motion: reduce) {
      &__tab::after {
        transition: none;
      }
    }
  }

  .main-part {
    padding-left: 60px;
    padding-top: 23px;

    border-radius: 4px 4px 0 0;
    background-color: var(--bg-secondary);
    box-shadow: 0 0.5px 0.5px 0 rgba(12,18,62,0.13);
  }

  .rest-part {
    max-height: 610px;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: var(--scrollbar-thumb) var(--scrollbar-track);
    padding-left: 60px;
    padding-top: 32px;
    padding-bottom: 60px;
    background-size: contain;
    background-position: right;
  }

  .cynergy_app{
    background-image: url("../../assets/icons/whitelabeling/cynergy.app/cynergy_app_half.svg");
  }
  .xmcyber_com{
    background-image: url("../../assets/icons/whitelabeling/xmcyber.com/xmcyber_com_half.svg");
  }
  .xmcyber_master_com{
    background-image: url("../../assets/icons/whitelabeling/xmcyber.com/xmcyber_com_half.svg");
  }

  ::-webkit-scrollbar-track {
    background-color: var(--scrollbar-track);
  }

  ::-webkit-scrollbar-thumb {
    border-radius: 10px;
    background-color: var(--scrollbar-thumb);
  }

  .security-risk {
    margin-top: 8px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .risk-code {
      vertical-align: top;
      display: inline-block;
      color: var(--text-secondary);
      font-size: $font-size-xl;
      font-weight: bold;
      letter-spacing: 0;
      line-height: $font-size-2xl;

      overflow: hidden;
    }

    .security-risk-description {
      display: inline-block;
      text-transform: capitalize;

      color: var(--text-primary);
      font-size: $font-size-xl;
      font-weight: bold;
      letter-spacing: 0;
      line-height: $font-size-2xl;

      width: rem(350);
    }

    .suppress-issue {
      margin-top: $space-2;
      margin-right: $space-5;
      display: inline-block;
      justify-self: right;
      .toggle-suppress-issue {
        background-color: var(--brand-primary) !important;
        border-radius: $radius-sm;
        box-shadow: $shadow-sm;
        color: var(--text-inverse) !important;
        font-family: $default-application-font-medium;
        font-size: $font-size-sm;
        height: $btn-height-md;
        letter-spacing: 1.07px;
        line-height: $font-size-md;
      }
    }
  }

  .security-risk-data-title {
    display: inline-block;
  }

  .action-button-container {

    display: inline-block;
    float: right;

    &.mark-issue-as-fixed-button-container {
      margin-top: 11px;
      margin-right: 27px;
    }

    &.edit-delete-issue-button-container {
      margin-top: 7px;
      margin-right: 60px;
    }
  }

  .data-title {
    color: var(--text-secondary);
    font-size: 12px;
    letter-spacing: 0.8px;
    line-height: 10px;
    text-transform: uppercase;
    margin-bottom: 6px;
  }

  .issue-status {
  }

  .upper-description-block {
    margin-top: 16px;
    display: block;
    .text-container {
      padding-right: 60px;
      color: var(--text-primary);
      font-size: 14px;
      letter-spacing: 0;
      line-height: 18px;
    }
  }

  .severity-risk-score-line {
    display: inline-block;
  }

  .issue-risk-score {
    margin-left: 8px;
  }

  .date-column {
    color: var(--text-secondary);
    font-size: 14px;
    letter-spacing: 0;
    line-height: 14px;
  }

  .description-block {
    margin-top: 9px;
    display: block;
    .text-container::first-letter {
      text-transform: capitalize;
    }
    .text-container {
      padding-right: 60px;
      font-size: 14px;
      letter-spacing: 0;
      line-height: 18px;
      padding-bottom: 36px;
    }
  }
  .solution-block {
    display: block;
    padding-bottom: $space-8;
    .bullet {
      flex: 1;
    }
    .text-container {
      padding-left: $space-1;
      padding-right: rem(60);
      color: var(--text-primary);
      font-size: $font-size-base;
      letter-spacing: 0;
      line-height: $font-size-lg;
      padding-bottom: $space-4;
    }
    .mitigation-option {
      display: flex;
    }
    .material-icons {
      font-size: $font-size-md;
      opacity: 0.5;
      margin-top: $space-1;
    }
  }

  .reference-block {
    padding-bottom: $space-8;
    display: block;
    .text-container {
      padding-right: rem(60);
      color: var(--text-primary);
      font-size: $font-size-base;
      letter-spacing: 0;
      line-height: $font-size-lg;
      padding-bottom: $space-4;
    }
    .url-section {
      display: flex;
      .bullet {
        padding-right: $space-1;
      }
      .material-icons {
        font-size: $font-size-md;
        opacity: 0.5;
        margin-top: rem(1);
      }
    }
  }

  .technical-block {
    padding-bottom: $space-8;
    display: block;
    .url-section {
      .bullet {
        padding-right: $space-1;
      }
      .text-container {
        padding-left: $space-1;
        padding-right: rem(60);
        color: var(--text-primary);
        font-size: $font-size-base;
        letter-spacing: 0;
        line-height: $font-size-lg;
        padding-bottom: $space-4;
        max-width: rem(700);
      }
      display: flex;
      .material-icons {
        font-size: $font-size-md;
        opacity: 0.5;
        margin-top: $space-1;
      }
    }
    .technical-data {
      padding-top: $space-4;
    }
  }

  .data-sub-title {
    font-size: $font-size-xs;
    color: var(--text-secondary);
    letter-spacing: 0.8px;
    line-height: $font-size-xs;
    text-transform: uppercase;
    margin-bottom: $space-1;
  }

  .data-row {
    margin-top: 25px;
    padding-bottom: 35px;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    row-gap: $space-4;
    .details-column {
      display: inline-block;
      width: fit-content;
      max-width: 420px;
      margin-right: 16px;
    }
    .data-column {
      display: inline-block;
      min-width: 90px;
      max-width: 120px;
      margin-right: 16px;
      .probability {
        line-height: unset;
      }
      // Status labels like "Temporary Exception" need their full width.
      &.issue-status {
        max-width: none;
        flex-shrink: 0;
        white-space: nowrap;
      }
    }

    .data-column-date {
      display: inline-block;
      width: 135px;
      margin-right: 16px;
    }
  }

  // Narrow screens: smaller gutters, title and action stack.
  @media (max-width: 700px) {
    .main-part,
    .rest-part {
      padding-left: $space-5;
    }
    .rest-part {
      padding-bottom: $space-8;
    }
    .security-risk {
      flex-wrap: wrap;
      gap: $space-2;
      padding-right: $space-5;
      .security-risk-description {
        width: auto;
        flex: 1 1 100%;
      }
      .suppress-issue {
        margin: 0;
      }
    }
    .suppression-summary {
      margin-right: $space-5;
      flex-wrap: wrap;
    }
    .issue-tabs {
      gap: $space-4;
      overflow-x: auto;
      scrollbar-width: none;
    }
  }
}

</style>
